import { useState } from 'react';
import { GroupBusinessSettings, GroupTemplate } from '../../../../types';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { UserKeys, saveGroupTemplate } from '../../../api/users-api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { handleErrors } from '../../../utils/errors';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import SharedDialog from '../../../shared/shared-dialog';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
interface CreateTemplateDialogProps {
  open: boolean;
  handleClose: () => void;
  groupEmail: string;
  groupSettings: GroupBusinessSettings;
  changeLogRoles: string[];
}

export default function CreateTemplateDialog({
  open,
  handleClose,
  groupSettings,
  groupEmail,
  changeLogRoles,
}: CreateTemplateDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: (data: GroupTemplate) =>
      saveGroupTemplate(data, changeLogMessage),
    onSuccess: (returnedData) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Group template created successfully!',
      });

      queryClient.setQueryData<GroupTemplate[] | undefined>(
        [UserKeys.GroupTemplates],
        (oldData) => (oldData ? [...oldData, returnedData] : [returnedData])
      );

      handleClose();
      setChangeLogMessage('');
      setTemplateName('');
      setTemplateDescription('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue creating the group template. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.directory.group_details.save_as_template, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.groups,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        DirectoryRoles.CreateGroupTemplate.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      const templateModel: GroupTemplate = {
        createdDate: 0,
        id: 0,
        groupTemplateName: templateName,
        groupTemplateDesc: templateDescription,
        ...groupSettings,
      };
      mutate(templateModel);
    }
  }

  return (
    <>
      <SharedDialog
        open={open}
        handleClose={() => {
          handleClose();
          setTemplateName('');
          setTemplateDescription('');
        }}
        title="Create Group Template"
        dialogContent={
          <Stack gap={2}>
            <Typography>
              Take current settings from <strong>{groupEmail}</strong> and
              create a group template.
            </Typography>
            <Stack gap={2}>
              <TextField
                id="name"
                label="Template Name"
                fullWidth
                required
                autoFocus
                autoComplete="off"
                value={templateName}
                onChange={(event) => setTemplateName(event.target.value)}
              />
              <TextField
                id="description"
                label="Template Description"
                fullWidth
                autoComplete="off"
                value={templateDescription}
                onChange={(event) => setTemplateDescription(event.target.value)}
              />
            </Stack>
          </Stack>
        }
        primaryButton={{
          title: 'Create Template',
          action: handleSubmit,
          loading: isPending,
          disabled: isPending || !templateName,
        }}
      />
      {openChangeLogDialog && (
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={() => {
            const templateModel: GroupTemplate = {
              createdDate: 0,
              id: 0,
              groupTemplateName: templateName,
              groupTemplateDesc: templateDescription,
              ...groupSettings,
            };
            mutate(templateModel);
          }}
          mutateLoading={isPending}
        />
      )}
    </>
  );
}
